import React from 'react';
import { Box, Typography, LinearProgress } from '@mui/material';
import IPhoneFrame, {
  StatusBar, NavBar, HomeBar,
  IOS_FONT, IOS_BLUE, IOS_GRAY_BG, IOS_GRAY_FIELD, IOS_GRAY_TEXT, IOS_SEPARATOR,
} from './IPhoneFrame';
import { STUDENT, APP_LOGOS, TEACHER } from '../sharedMockData';

/**
 * User tapped Khan tile → Layer 3 injection in flight → Khan rendered in child WKWebView.
 * Side-by-side inside one phone frame: left half is the "preparing" sheet, right half is
 * the landed webview. Actually: we only have one phone frame — show the loading state as
 * the primary, with a smaller inset preview of the landed state to convey the transition.
 */
const IosPartnerLaunchMockup = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, p: 3, bgcolor: '#eceff1', minHeight: '100%', flexWrap: 'wrap' }}>
    {/* Phase A — "Preparing Khan Academy…" */}
    <IPhoneFrame
      title="3a. Preparing Khan (Layer 3 inject-login in flight)"
      caption="Short-lived JS snippet resolving via POST /api/mobile/inject-login. Credential lives <1s in the WKWebView JS heap."
    >
      <StatusBar />
      <NavBar
        title="Khan Academy"
        leadingButton={{ chevron: true, label: 'Apps' }}
        trailingButton={{ label: '⋯' }}
      />
      <Box sx={{
        position: 'absolute',
        top: 44 + 44, left: 0, right: 0, bottom: 0,
        bgcolor: '#fff',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', px: 4,
      }}>
        {/* Khan logo — real CDN asset */}
        <Box sx={{
          width: 88, height: 88, borderRadius: '22px',
          bgcolor: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          mb: 3, overflow: 'hidden',
          boxShadow: '0 8px 24px rgba(20, 191, 150, 0.25)',
          border: `0.5px solid ${IOS_SEPARATOR}`,
        }}>
          <Box component="img" src={APP_LOGOS.khan} alt="Khan Academy" sx={{ width: 74, height: 74, objectFit: 'contain' }} />
        </Box>

        <Typography sx={{
          fontFamily: IOS_FONT, fontSize: '22px', fontWeight: 600, color: '#000', mb: 0.8,
        }}>Preparing Khan Academy…</Typography>

        <Typography sx={{
          fontFamily: IOS_FONT, fontSize: '14px', color: IOS_GRAY_TEXT,
          textAlign: 'center', mb: 4, maxWidth: 280,
        }}>
          Securely signing you in. Your credentials never leave this device after the
          login completes.
        </Typography>

        {/* iOS-style spinner / progress */}
        <Box sx={{
          width: 220,
          height: 4,
          bgcolor: IOS_GRAY_FIELD,
          borderRadius: 2,
          overflow: 'hidden',
          mb: 3,
        }}>
          <Box sx={{
            width: '62%', height: '100%', bgcolor: IOS_BLUE, borderRadius: 2,
          }} />
        </Box>

        {/* Steps checklist */}
        <Box sx={{ width: '100%', maxWidth: 280 }}>
          {[
            { label: 'Fetched session', done: true },
            { label: 'Resolved credentials', done: true },
            { label: 'Injecting login form', done: false, running: true },
            { label: 'Awaiting Khan dashboard', done: false },
          ].map((s, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1 }}>
              <Box sx={{
                width: 18, height: 18, borderRadius: '50%',
                bgcolor: s.done ? IOS_BLUE : (s.running ? '#FFCC00' : IOS_GRAY_FIELD),
                color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '11px', fontWeight: 700,
              }}>{s.done ? '✓' : (s.running ? '…' : '')}</Box>
              <Typography sx={{
                fontFamily: IOS_FONT, fontSize: '14px',
                color: s.done || s.running ? '#000' : IOS_GRAY_TEXT,
              }}>{s.label}</Typography>
            </Box>
          ))}
        </Box>

        <Typography sx={{
          fontFamily: IOS_FONT, fontSize: '11px', color: IOS_GRAY_TEXT, mt: 4,
        }}>
          Layer 3 · WKWebView JS injection
        </Typography>
      </Box>
      <HomeBar />
    </IPhoneFrame>

    {/* Phase B — Logged into Khan inside child WKWebView */}
    <IPhoneFrame
      title="3b. Khan landed (child WKWebView)"
      caption="User signed in to khanacademy.org inside the child WKWebView. Chevron back returns to the AchieveDXP dashboard."
    >
      <StatusBar />
      <NavBar
        title="khanacademy.org"
        leadingButton={{ chevron: true, label: 'Apps' }}
        trailingButton={{ label: '⋯' }}
        subtitle={`🔒 Signed in as ${STUDENT.khanUser}`}
      />
      <Box sx={{
        position: 'absolute',
        top: 44 + 44, left: 0, right: 0, bottom: 0,
        bgcolor: '#F7F8FA',
        overflow: 'auto',
      }}>
        {/* Fake Khan chrome */}
        <Box sx={{
          bgcolor: '#14BF96', color: '#fff', px: 2, py: 1.5,
          display: 'flex', alignItems: 'center', gap: 1.5,
        }}>
          <Box sx={{
            width: 28, height: 28, borderRadius: 1,
            bgcolor: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            overflow: 'hidden',
          }}>
            <Box component="img" src={APP_LOGOS.khan} alt="Khan" sx={{ width: 24, height: 24, objectFit: 'contain' }} />
          </Box>
          <Typography sx={{ fontFamily: IOS_FONT, fontSize: '16px', fontWeight: 700 }}>
            Khan Academy
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Box sx={{
            width: 32, height: 32, borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '13px', fontWeight: 700,
          }}>{STUDENT.initials}</Box>
        </Box>

        <Box sx={{ p: 2 }}>
          <Typography sx={{
            fontFamily: IOS_FONT, fontSize: '20px', fontWeight: 700, mb: 0.5,
          }}>Welcome back, {STUDENT.full.split(' ')[0]}!</Typography>
          <Typography sx={{ fontFamily: IOS_FONT, fontSize: '13px', color: IOS_GRAY_TEXT, mb: 2 }}>
            Keep going with Algebra 1
          </Typography>

          <Box sx={{ bgcolor: '#fff', p: 2, borderRadius: 2, mb: 1.5, boxShadow: '0 1px 2px rgba(0,0,0,0.06)' }}>
            <Typography sx={{ fontFamily: IOS_FONT, fontSize: '11px', color: '#14BF96', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
              Up Next
            </Typography>
            <Typography sx={{ fontFamily: IOS_FONT, fontSize: '15px', fontWeight: 600 }}>
              Solving linear equations
            </Typography>
            <LinearProgress variant="determinate" value={68} sx={{ mt: 1, height: 6, borderRadius: 3, bgcolor: '#E8F5F0', '& .MuiLinearProgress-bar': { bgcolor: '#14BF96' } }} />
            <Typography sx={{ fontFamily: IOS_FONT, fontSize: '11px', color: IOS_GRAY_TEXT, mt: 0.5 }}>
              68% complete
            </Typography>
          </Box>

          <Box sx={{ bgcolor: '#fff', p: 2, borderRadius: 2, boxShadow: '0 1px 2px rgba(0,0,0,0.06)' }}>
            <Typography sx={{ fontFamily: IOS_FONT, fontSize: '14px', fontWeight: 600, mb: 0.5 }}>
              Today's assignments
            </Typography>
            <Typography sx={{ fontFamily: IOS_FONT, fontSize: '12px', color: IOS_GRAY_TEXT }}>
              2 new from {TEACHER.short}
            </Typography>
          </Box>
        </Box>
      </Box>
      <HomeBar />
    </IPhoneFrame>
  </Box>
);

export default IosPartnerLaunchMockup;
